import { CURRENT_VOTE } from "@/constants/votes";
import {
    useDeleteVoteMutation,
    useGetActiveVoteQuery,
    useGetVoteCountsQuery,
    useSubmitVoteMutation
} from "@/graphql/generated";
import { Vote, VoteState } from "@/types/vote";
import { fireEvent } from "lib/ga";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "./useAuth";

const STORAGE_KEY = `mix_vote_${CURRENT_VOTE.id}`;

/**
 * Hook for managing vote data and state
 *
 * Phase 2: Integrated with backend API for authenticated votes
 * - Guests: localStorage only (privacy-friendly)
 * - Authenticated: Database + localStorage sync
 *
 * Both VotingCard and VotingPage use this hook to stay in sync
 */
export function useVoteData() {
    const { me, isLoggedIn } = useAuth();
    const [localVoteState, setLocalVoteState] = useState<VoteState>({
        hasVoted: false,
        selectedOption: null
    });

    // Fetch active vote from API (for both authenticated and guest users)
    const {
        data: voteData,
        loading: voteLoading,
        refetch: refetchVote
    } = useGetActiveVoteQuery({
        variables: { userId: me?.id, includeMyVote: !!me?.id },
        skip: false, // Always fetch the active vote
        fetchPolicy: "cache-and-network"
    });

    // Fetch vote counts for percentages
    const activeVote = voteData?.mix_vote?.[0];
    const { data: countsData, refetch: refetchCounts } = useGetVoteCountsQuery({
        variables: { voteId: activeVote?.id || "" },
        skip: !activeVote?.id,
        fetchPolicy: "cache-and-network"
    });

    // GraphQL mutations
    const [submitVoteMutation] = useSubmitVoteMutation();
    const [deleteVoteMutation] = useDeleteVoteMutation();

    // Load vote state from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const data = JSON.parse(stored);
                setLocalVoteState({
                    hasVoted: data.is_voted,
                    selectedOption: data.selected_option,
                    timestamp: data.timestamp
                });
            }
        } catch (error) {
            console.error("Failed to load vote data:", error);
        }
    }, []);

    // Sync API vote state with local state for authenticated users
    const voteState = useMemo<VoteState>(() => {
        if (isLoggedIn && activeVote) {
            const myVote = activeVote.my_vote?.[0];
            if (myVote) {
                return {
                    hasVoted: true,
                    selectedOption: myVote.selected_option,
                    timestamp: new Date(myVote.created_at).getTime()
                };
            }
            return { hasVoted: false, selectedOption: null };
        }
        // Guest users: use localStorage
        return localVoteState;
    }, [isLoggedIn, activeVote, localVoteState]);

    // Calculate percentages from vote counts (with client-side adjustment for guest votes)
    const { percentages, totalVotes } = useMemo<{
        percentages: [number, number];
        totalVotes: number;
    }>(() => {
        if (!countsData?.mix_user_vote_count)
            return { percentages: [50, 50], totalVotes: 0 }; // Default 50-50

        const counts = countsData.mix_user_vote_count;
        const option0Count =
            counts.find((c) => c.selected_option === 0)?.vote_count || 0;
        const option1Count =
            counts.find((c) => c.selected_option === 1)?.vote_count || 0;

        // For guest users who have voted: add their vote to the counts client-side
        let adjustedOption0 = option0Count;
        let adjustedOption1 = option1Count;

        if (
            !isLoggedIn &&
            voteState.hasVoted &&
            voteState.selectedOption !== null
        ) {
            if (voteState.selectedOption === 0) {
                adjustedOption0 += 1;
            } else {
                adjustedOption1 += 1;
            }
        }

        // Calculate percentages from adjusted counts
        const total = adjustedOption0 + adjustedOption1;
        if (total === 0) return { percentages: [50, 50], totalVotes: 0 };

        const option0Percentage =
            Math.round((adjustedOption0 / total) * 1000) / 10;
        const option1Percentage =
            Math.round((adjustedOption1 / total) * 1000) / 10;

        return {
            percentages: [option0Percentage, option1Percentage],
            totalVotes: total
        };
    }, [countsData, isLoggedIn, voteState]);

    // Total participant count - use the same total from percentage calculation to ensure consistency
    const participantCount = useMemo(() => {
        // If we have vote counts data, use the total from percentage calculation
        if (countsData?.mix_user_vote_count && totalVotes > 0) {
            return totalVotes;
        }

        // Fallback to aggregate count from vote query
        if (activeVote) {
            const baseCount =
                activeVote.mix_user_votes_aggregate?.aggregate?.count || 0;

            // For guest users who have voted: add 1 to participant count client-side
            if (!isLoggedIn && voteState.hasVoted) {
                return baseCount + 1;
            }

            return baseCount;
        }

        // Final fallback to static data
        return CURRENT_VOTE.participantCount;
    }, [countsData, totalVotes, activeVote, isLoggedIn, voteState.hasVoted]);

    /**
     * Submit a vote for the given option
     * - Guests: localStorage only
     * - Authenticated: Database + localStorage sync
     */
    const submitVote = useCallback(
        async (optionId: number) => {
            const newState: VoteState = {
                hasVoted: true,
                selectedOption: optionId,
                timestamp: Date.now()
            };

            // Always save to localStorage for offline support
            try {
                localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({
                        is_voted: true,
                        selected_option: optionId,
                        timestamp: newState.timestamp
                    })
                );
                setLocalVoteState(newState);

                // Track analytics
                fireEvent({
                    event: "vote" as any, // Custom event type for vote tracking
                    params: {
                        vote_id: activeVote?.id || CURRENT_VOTE.id,
                        vote_question:
                            activeVote?.question || CURRENT_VOTE.question,
                        selected_option_id: optionId,
                        selected_option_text:
                            (activeVote
                                ? optionId === 0
                                    ? activeVote.first_option
                                    : activeVote.second_option
                                : CURRENT_VOTE.options[optionId]?.text) || ""
                    }
                } as any);

                // If authenticated, also save to database
                if (isLoggedIn && me?.id && activeVote?.id) {
                    await submitVoteMutation({
                        variables: {
                            voteId: activeVote.id,
                            selectedOption: optionId
                        },
                        optimisticResponse: {
                            insert_mix_user_vote_one: {
                                __typename: "mix_user_vote",
                                id: "temp-id",
                                selected_option: optionId,
                                created_at: new Date().toISOString(),
                                updated_at: new Date().toISOString()
                            }
                        }
                    });
                }

                // Refetch to get updated counts for ALL users (guest + authenticated)
                // Guest users see real vote data without inserting to DB
                await Promise.all([refetchVote(), refetchCounts()]);
            } catch (error) {
                console.error("Failed to save vote:", error);
                throw error;
            }
        },
        [
            isLoggedIn,
            me?.id,
            activeVote,
            submitVoteMutation,
            refetchVote,
            refetchCounts
        ]
    );

    /**
     * Reset the vote (allows user to vote again)
     * Primarily used in VotingPage for "다시 선택하기" functionality
     */
    const reset = useCallback(async () => {
        try {
            // Remove from localStorage
            localStorage.removeItem(STORAGE_KEY);
            setLocalVoteState({
                hasVoted: false,
                selectedOption: null
            });

            // If authenticated, also delete from database
            if (isLoggedIn && me?.id && activeVote?.id) {
                await deleteVoteMutation({
                    variables: {
                        voteId: activeVote.id
                    },
                    optimisticResponse: {
                        delete_mix_user_vote: {
                            __typename: "mix_user_vote_mutation_response",
                            affected_rows: 1
                        }
                    }
                });

                // Refetch to get updated counts and user's vote
                await Promise.all([refetchVote(), refetchCounts()]);
            }
        } catch (error) {
            console.error("Failed to reset vote:", error);
            throw error;
        }
    }, [
        isLoggedIn,
        me?.id,
        activeVote,
        deleteVoteMutation,
        refetchVote,
        refetchCounts
    ]);

    // Build vote object with real or static data
    const vote = useMemo<Vote>(() => {
        // Use fetched vote data for both authenticated and guest users
        if (activeVote) {
            return {
                id: activeVote.id,
                question: activeVote.question,
                description: activeVote.description || undefined,
                options: [
                    { id: 0, text: activeVote.first_option },
                    { id: 1, text: activeVote.second_option }
                ],
                participantCount
            };
        }
        // Fallback to static data if vote hasn't loaded yet
        return CURRENT_VOTE;
    }, [activeVote, participantCount]);

    // Show loading state for both authenticated and guest users
    const isLoading = voteLoading;

    return {
        vote,
        voteState,
        percentages,
        participantCount,
        submitVote,
        reset,
        isLoading
    };
}
